import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isEqual } from '@comparit/core';
import type { Observable } from 'rxjs';
import {
  BehaviorSubject,
  EMPTY,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
  tap,
} from 'rxjs';
import type { ResponseStateHandler } from '../interfaces/response-state-handler.type';
import type {
  SchutzOptimierenForm,
  SchutzOptimierenKatalogInput,
} from './schutz-optimieren-form.class';

/** Earlier steps emit on every keystroke; the catalogue is only worth asking for once they settle. */
const DEBOUNCE_MS = 400;

/**
 * Keeps the "Schutz optimieren" catalogue in step with the funnel for the lifetime of the app: as
 * soon as the Vorgaben are complete it fetches, and it fetches again only when they change. State
 * is held here, not in a step, so any screen after the funnel can read it. Same shape as
 * `TaskSyncBaseService`: the app subclass supplies the generated transport, this lib names no
 * generated type.
 */
@Injectable()
export abstract class SchutzOptimierenKatalogBaseService<
  TVorgaben,
  TKatalog extends SchutzOptimierenKatalogInput,
> {
  private readonly destroyRef = inject(DestroyRef);

  /** The Vorgaben without `schutzOptimieren` — a tile toggle must not re-fetch. */
  protected abstract readonly vorgaben$: Observable<TVorgaben>;

  /** Where the catalogue's attributes become controls. */
  protected abstract readonly form: SchutzOptimierenForm<string>;

  /** True once the funnel is complete enough that the backend will not answer 400. */
  protected abstract canFetch(): boolean;

  protected abstract fetchKatalog(vorgaben: TVorgaben): Observable<TKatalog>;

  readonly state = signal<ResponseStateHandler<TKatalog>['state']>('none');
  readonly katalog = signal<TKatalog | null>(null);

  private readonly reload$ = new BehaviorSubject(0);
  private started = false;

  init(): void {
    if (this.started) return;
    this.started = true;

    combineLatest([this.vorgaben$, this.reload$])
      .pipe(
        filter(() => this.canFetch()),
        debounceTime(DEBOUNCE_MS),
        // The reload counter is part of the tuple, so a retry with unchanged Vorgaben still passes.
        distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
        tap(() => this.state.set('loading')),
        switchMap(([vorgaben]) =>
          this.fetchKatalog(vorgaben).pipe(
            tap(katalog => {
              this.form.setKatalog(katalog);
              this.katalog.set(katalog);
              this.state.set('success');
            }),
            catchError(() => {
              this.state.set('error');
              return EMPTY;
            })
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  reload(): void {
    this.reload$.next(this.reload$.value + 1);
  }
}
